import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "../api/client.js";
import { FinishedJobRow } from "../components/FinishedJobRow.js";
import { OpsHistoryShell } from "../components/OpsHistoryShell.js";

/**
 * Every finished job, filterable by kind, result and subject: what happened to this
 * filing, did anything fail on a given day, has the registry been refreshing.
 *
 * **No composite health number**: a number would become the thing that is read instead of
 * the rows. The filters narrow; they do not rank.
 */
export function JobHistoryPage() {
  const [kind, setKind] = useState("");
  const [state, setState] = useState<"" | "done" | "failed">("");
  /**
   * Two pieces of state for one box: `draft` is what the reader is typing and `q` is what
   * has been asked for, so a search runs on submit rather than on every keystroke.
   */
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const kinds = useQuery({ queryKey: ["ops", "history", "kinds"], queryFn: api.opsHistoryKinds });

  const history = useInfiniteQuery({
    queryKey: ["ops", "history", { kind, state, q }],
    queryFn: ({ pageParam }) =>
      api.opsHistory({
        ...(kind ? { kind } : {}),
        ...(state ? { state } : {}),
        ...(q ? { q } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: "" as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = useMemo(
    () => history.data?.pages.flatMap((p) => p.items) ?? [],
    [history.data],
  );
  const total = history.data?.pages[0]?.total ?? null;

  return (
    <OpsHistoryShell
      title="Finished jobs"
      deck="Every job that has recorded a finish time, newest first. A successful job keeps its row — nothing here is a reconstruction."
      count={
        total === null
          ? `${items.length} shown`
          : `${items.length} of ${total.toLocaleString()} matching job${total === 1 ? "" : "s"}`
      }
      note={
        <>
          {(kind || state || q) && " — filtered"}
          {/*
            A cap that was reached is said. A truncated result set looks exactly like a
            complete one, and on a page whose purpose is finding out what happened,
            "nothing else matched" and "I stopped looking" are opposite answers.
          */}
          {history.data?.pages[0]?.truncatedMatches && (
            <>
              {" "}
              <span className="histtrunc">
                That term matches more companies than this search will resolve — narrow it.
              </span>
            </>
          )}
        </>
      }
      isPending={history.isPending}
      error={history.isError ? (history.error as Error) : null}
      isEmpty={items.length === 0}
      emptyFiltered={Boolean(kind || state || q)}
      emptyAll="No job has recorded a finish time yet."
      onLoadMore={history.hasNextPage ? () => void history.fetchNextPage() : null}
      loadingMore={history.isFetchingNextPage}
      filters={
        <form
          className="histfilters"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(draft.trim());
          }}
        >
          <label>
            <span className="k">Kind</span>
            {/*
              Only the kinds that have run. Offering one that never has reads as a pipeline
              stage that is broken.
            */}
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">any</option>
              {(kinds.data?.kinds ?? []).map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="k">Result</span>
            <select
              value={state}
              onChange={(e) => setState(e.target.value as "" | "done" | "failed")}
            >
              <option value="">any</option>
              <option value="done">done</option>
              <option value="failed">failed</option>
            </select>
          </label>
          <label className="histsearch">
            <span className="k">Company, ticker, accession or situation</span>
            <input
              value={draft}
              placeholder="SNDK, SanDisk, 0001104659-25-010902…"
              onChange={(e) => setDraft(e.target.value)}
            />
          </label>
          <button type="submit" className="iconbtn">
            Search
          </button>
          {(kind || state || q) && (
            <button
              type="button"
              className="iconbtn"
              onClick={() => {
                setKind("");
                setState("");
                setDraft("");
                setQ("");
              }}
            >
              Clear
            </button>
          )}
        </form>
      }
    >
      {items.map((job) => (
        <FinishedJobRow
          key={job.id}
          job={job}
          open={openId === job.id}
          onToggle={() => setOpenId(openId === job.id ? null : job.id)}
        />
      ))}
    </OpsHistoryShell>
  );
}
